// frontend/src/components/shared/ScrollToTop.tsx
// Global scroll-to-top on route change - fixes footer navigation issue

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop Component
 * 
 * Automatically scrolls to the top of the page when the route changes.
 * This fixes the common React Router issue where navigating between pages
 * keeps the scroll position at the bottom (especially annoying when coming from footer).
 * 
 * Industry standard implementation used by Stripe, Linear, Notion, etc.
 */
const ScrollToTop: React.FC = () => {
    const { pathname, search } = useLocation();

    // Enforce manual scroll restoration (prevents browsers from keeping previous offset, especially on mobile)
    useEffect(() => {
        const { history } = window;
        const previous = history.scrollRestoration;
        try {
            history.scrollRestoration = 'manual';
        } catch {
            /* ignore */
        }
        return () => {
            try {
                history.scrollRestoration = previous || 'auto';
            } catch {
                /* ignore */
            }
        };
    }, []);

    // Scroll on initial mount (fixes login redirect landing at footer)
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    // Scroll on route or query change (covers footer links, login/signup redirects, and same-path query nav)
    useEffect(() => {
        // Use rAF to ensure layout is ready, then force top
        requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        });
    }, [pathname, search]);

    return null; // This component doesn't render anything
};

export default ScrollToTop;
